import { useState } from 'react'

import { useErrorHandler } from '../../hooks/useErrorHandler'
import { usePSNavigate } from '../../hooks/usePSNavigate'
import { icons } from '../../icons/desktop-v2'
import { RecordFactory } from '../../models/RecordFactory'
import { Source } from '../../models/Source'
import { ComponentData } from '../../utils/typing'
import { Icon, IconName } from '../Icon'
import { PocketComponentProps, usePSComponent } from '../PocketComponent'
import { Spinner } from '../Spinner'
import Button from '../app/design/Button'
import Heading from '../app/design/Heading'
import Modal from '../app/design/Modal'
import Paragraph from '../app/design/Paragraph'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../app/design/Table'
import { ListHeader } from '../common/ListHeader'
import { Select } from '../common/fields/Select'
import { BankModal } from './BankModal'
import { CardModal } from './CardModal'
import { AuthorizeSourceModal } from './AuthorizeSourceModal'
import { VerifySourceModal } from './VerifySourceModal'

const viewWillAppear = async () => {
    return {
        sources: await Source.validSources(),
    }
}

const render = ({ data, base }: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    const navigate = usePSNavigate()
    const { logAndDisplayError } = useErrorHandler()

    const [sourceToDelete, setSourceToDelete] = useState<Source | null>(null)
    const [sourceToAuthorize, setSourceToAuthorize] = useState<Source | null>(null)
    const [sourceToVerify, setSourceToVerify] = useState<Source | null>(null)

    const [isProcessing, setIsProcessing] = useState(false)
    const [modal, setModal] = useState<'checking' | 'card' | null>(null)

    const [sources, setSources] = useState<Source[]>(
        (data.sources || []).sort((a, b) => a.displayTitle.localeCompare(b.displayTitle))
    )

    const isLastCard =
        sources.filter((source) => source.type === 'debit' || source.type === 'credit').length === 1

    const onDeleteSource = async () => {
        if (sourceToDelete?.id) {
            setIsProcessing(true)
            const result = await RecordFactory.getInstance().deleteSource(sourceToDelete.id)
            setIsProcessing(false)
            setSourceToDelete(null)

            if (!result.ok) {
                logAndDisplayError(result.error)
                return
            }

            base.reload()
        }
    }

    const doAuthorize = async (accountNumber: string) => {
        if (!accountNumber || !sourceToAuthorize) return
        setIsProcessing(true)
        const result = await RecordFactory.getInstance().submitAuthorization(
            sourceToAuthorize,
            accountNumber
        )
        setIsProcessing(false)

        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }

        base.reload()
    }

    const doVerify = async (source: Source, firstAmount: number, secondAmount: number) => {
        if (!source || !firstAmount || !secondAmount) return
        setIsProcessing(true)
        const result = await RecordFactory.getInstance().submitVerificationBySource(
            source,
            firstAmount,
            secondAmount
        )
        setIsProcessing(false)

        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }

        base.reload()
    }

    return (
        <div className="relative h-screen w-full overflow-y-auto p-8 pt-6">
            <ListHeader
                title={
                    <>
                        Payment Methods{' '}
                        <span className="text-base leading-5 font-bold text-gray-600">
                            ({sources.length})
                        </span>
                    </>
                }
                backButton={{
                    label: 'Settings',
                    href: '/settings',
                }}
                actionButton={{
                    icon: 'plus.small',
                    intent: 'primary',
                    buttonLabel: 'Add',
                    actions: [
                        {
                            id: 'card',
                            label: 'Add a card',
                            icon: 'bankCard',
                            onClick: () => {
                                setModal('card')
                            },
                        },
                        {
                            id: 'checking',
                            label: 'Add a checking account',
                            icon: 'bank',
                            onClick: () => {
                                setModal('checking')
                            },
                        },
                    ],
                }}
            />
            <div className="mt-6 rounded-lg bg-white p-8">
                {sources.length === 0 ? (
                    <div className="flex flex-col items-center justify-center gap-6 rounded-lg bg-white p-16">
                        <Paragraph>
                            Add a payment method to make or receive payments. For instant payouts{' '}
                            <span className="inline-block">⚡</span>, add a debit card and toggle on
                            "Accept instant payouts".
                        </Paragraph>
                    </div>
                ) : (
                    <div>
                        <div className="pb-6 text-sm text-gray-500">
                            Add a payment method to make or receive payments. For instant payouts,
                            add a debit card and toggle on “Accept instant payouts.”
                        </div>
                        <Table>
                            <TableHeader>
                                <TableRow location="head">
                                    <TableHead>Name</TableHead>
                                    <TableHead className="font-normal">Bank</TableHead>
                                    <TableHead className="font-normal">Status</TableHead>
                                    <TableHead className="font-normal">Type</TableHead>
                                    <TableHead></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {sources.map((source) => {
                                    const sourceOptions = [
                                        {
                                            id: 'delete',
                                            title: 'Remove',
                                            icon: 'trash',
                                        },
                                    ]

                                    return (
                                        <TableRow key={source.id}>
                                            <TableCell>
                                                <div className="flex items-center">
                                                    <div className="mr-4 flex min-h-9 items-center pr-2">
                                                        {source.icon}
                                                    </div>
                                                    <span className="font-bold">
                                                        {source.type === 'checking'
                                                            ? `${source.type.charAt(0).toUpperCase() + source.type.slice(1)} - ${source.accountNumber?.slice(-4)}`
                                                            : `${source.cardBrand || source.type} - ${source.cardNumber?.slice(-4) || ''}`}
                                                    </span>
                                                </div>
                                            </TableCell>
                                            <TableCell>{source.bankName}</TableCell>
                                            <TableCell>{source.displayExpiresString}</TableCell>
                                            <TableCell>
                                                {source.instant
                                                    ? 'Instant debit'
                                                    : source.type
                                                      ? source.type.charAt(0).toUpperCase() +
                                                        source.type.slice(1)
                                                      : ''}
                                            </TableCell>
                                            <TableCell className="w-10">
                                                <Select
                                                    align="end"
                                                    size="small"
                                                    trigger={
                                                        <Button
                                                            size="small"
                                                            shape="square"
                                                            intent="tertiary_deprecated"
                                                        >
                                                            <Icon
                                                                icon="moreHorizontal.medium"
                                                                className="text-gray-500"
                                                            />
                                                        </Button>
                                                    }
                                                    options={[
                                                        {
                                                            id: 'authorize',
                                                            title: 'Authorize for ACH',
                                                            icon: 'dollar' as IconName,
                                                            condition:
                                                                source.type === 'checking' &&
                                                                !source.verifiedDate &&
                                                                !source.charge,
                                                        },
                                                        {
                                                            id: 'verify',
                                                            title: 'Verify for ACH',
                                                            icon: 'checkmark' as IconName,
                                                            condition:
                                                                source.type === 'checking' &&
                                                                !source.verifiedDate &&
                                                                source.charge,
                                                        },
                                                        {
                                                            id: 'delete',
                                                            title: 'Remove',
                                                            icon: 'trash' as IconName,
                                                            isDanger: true,
                                                            condition: true,
                                                        },
                                                    ]
                                                        .filter((option) => option.condition)
                                                        .map(({ condition, ...rest }) => rest)}
                                                    onSelectOption={(option) => {
                                                        if (option.id === 'delete')
                                                            setSourceToDelete(source)
                                                        if (option.id === 'authorize')
                                                            setSourceToAuthorize(source)
                                                        else if (option.id === 'verify')
                                                            setSourceToVerify(source)
                                                    }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    )
                                })}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </div>

            <CardModal
                isOpen={modal === 'card'}
                onClose={() => setModal(null)}
                forProOnly
                onSuccess={{
                    action: 'create-source',
                    client: undefined,
                    callback: (source) => {
                        setSources([...sources, source])
                        setModal(null)
                    },
                }}
            />
            <BankModal
                isOpen={modal === 'checking'}
                onClose={() => setModal(null)}
                onSuccess={(source) => {
                    setSources([...sources, source])
                    setModal(null)
                }}
            />

            <AuthorizeSourceModal
                source={sourceToAuthorize}
                isOpen={!!sourceToAuthorize}
                onClose={() => setSourceToAuthorize(null)}
                onAuthorize={doAuthorize}
                isProcessing={isProcessing}
            />
            <Modal
                title="Your account is being authorized"
                isOpen={!!sourceToAuthorize?.charge}
                onClose={() => setSourceToAuthorize(null)}
                footer={
                    <div className="items-right flex gap-x-4">
                        <Button onClick={() => setSourceToAuthorize(null)}>Done</Button>
                    </div>
                }
            >
                Two small "VERIFICATION" deposits will be sent to this account in 2-3 days
            </Modal>

            <VerifySourceModal
                isOpen={!!sourceToVerify}
                onClose={() => setSourceToVerify(null)}
                onVerify={(firstAmount: number, secondAmount: number) => {
                    if (sourceToVerify) doVerify(sourceToVerify, firstAmount, secondAmount)
                }}
                isProcessing={isProcessing}
            />
            <Modal
                title="Your account is now verified"
                isOpen={!!sourceToVerify?.verifiedDate}
                onClose={() => setSourceToVerify(null)}
                footer={
                    <div className="items-right flex gap-x-4">
                        <Button onClick={() => setSourceToVerify(null)}>Done</Button>
                    </div>
                }
            >
                You’re ready to begin making payments from this account.
            </Modal>

            <Modal
                isOpen={!!sourceToDelete}
                onClose={() => setSourceToDelete(null)}
                title={isLastCard ? 'Card required for payouts' : 'Remove payment method'}
                footer={
                    <div className="items-right flex gap-x-4">
                        <Button disabled={isProcessing} onClick={() => setSourceToDelete(null)}>
                            Cancel
                        </Button>
                        <Button
                            intent="destructive"
                            disabled={isProcessing}
                            onClick={onDeleteSource}
                        >
                            Remove
                        </Button>
                    </div>
                }
            >
                {isProcessing ? (
                    <div className="flex items-center justify-center">
                        <Spinner size={64} />
                    </div>
                ) : (
                    <Paragraph>
                        {isLastCard
                            ? 'In order to receive payouts, a debit or credit card and a checking account must be on file'
                            : `Are you sure you want to remove ${sourceToDelete?.displayTitle || 'this payment method'}?`}
                    </Paragraph>
                )}
            </Modal>
        </div>
    )
}

export const SourcesPage = usePSComponent(render, viewWillAppear)
